/*
 * Neuigkeiten des Projekts bearbeiten (HmiNote): der Hinweis "Was ist neu",
 * den das Panel nach dem Ausliefern zeigt, bis jemand ihn wegtippt.
 *
 * Eine neue Neuigkeit bekommt eine neue Kennung; nur eine neue Kennung zeigt
 * den Hinweis am Panel wieder. Eine bestehende zu korrigieren, laesst sie
 * deshalb fuer alle, die sie schon gelesen haben, weg.
 */

import { useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import type { HmiNote } from '../widgets';
import './NotesDialog.css';

const EXAMPLE = 'Wir haben Ihr Bedienfeld aktualisiert.\nNeu:\n- …\nBehoben:\n- …';

function newId(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `neu-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

export function NotesDialog({ notes, disabled, onChange, onClose }: {
  notes: readonly HmiNote[];
  disabled: boolean;
  onChange(next: HmiNote[]): void;
  onClose(): void;
}) {
  const [draft, setDraft] = useState<HmiNote>(() => ({
    id: newId(), title: 'Ihre Anzeige wurde verbessert', text: EXAMPLE, date: new Date().toISOString().slice(0, 10),
  }));
  const valid = draft.title.trim().length > 0 && draft.text.trim().length > 0;

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal hv-modal--wide hv-notes" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Neuigkeiten für das Panel</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <p class="hv-modal__hint">
            Nach dem Speichern zeigt das Panel jede neue Neuigkeit über der Anzeige, bis jemand
            „Verstanden“ tippt. Danach kommt sie dort nicht wieder, auch nicht nach einem Neustart.
            Zeilen mit „- “ werden Aufzählung, eine Zeile mit „:“ am Ende Zwischenüberschrift.
          </p>

          {!disabled && (
            <section class="hv-notes__new">
              <h3>Neue Neuigkeit</h3>
              <label class="hv-modal__field">
                <span>Überschrift</span>
                <input value={draft.title} onInput={(e) => setDraft({ ...draft, title: (e.target as HTMLInputElement).value })} />
              </label>
              <label class="hv-modal__field">
                <span>Text</span>
                <textarea rows={8} value={draft.text}
                          onInput={(e) => setDraft({ ...draft, text: (e.target as HTMLTextAreaElement).value })} />
              </label>
              <label class="hv-modal__field hv-notes__date">
                <span>Datum</span>
                <input type="date" value={draft.date ?? ''} onInput={(e) => setDraft({ ...draft, date: (e.target as HTMLInputElement).value || undefined })} />
              </label>
              <button type="button" class="hv-btn hv-btn--primary" disabled={!valid}
                      onClick={() => {
                        onChange([{ ...draft, title: draft.title.trim(), text: draft.text.trim() }, ...notes]);
                        setDraft({ ...draft, id: newId() });
                      }}>
                Hinzufügen
              </button>
              <span class="hv-modal__hint"> Erscheint am Panel, sobald das Projekt gespeichert ist.</span>
            </section>
          )}

          <h3 class="hv-notes__h3">Bisher ({notes.length})</h3>
          {notes.length === 0 && <p class="muted">Noch keine.</p>}
          <ul class="hv-notes__list">
            {notes.map((n) => (
              <li key={n.id}>
                <div class="hv-notes__row">
                  <b>{n.title}</b>
                  <span class="muted">{n.date ?? ''} · {n.id}</span>
                  {!disabled && (
                    <button type="button" class="hv-btn hv-btn--sm"
                            onClick={() => onChange(notes.filter((x) => x.id !== n.id))}>Entfernen</button>
                  )}
                </div>
                <pre class="hv-notes__text">{n.text}</pre>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>,
    document.body,
  );
}
